import { FC, useMemo } from 'react'
import { DisponibilidadeDia, HorarioDisponivel } from '../core/_models'
import {
  ESTADO_HORARIO_ICONES,
  ESTADO_HORARIO_LABELS,
  EstadoHorario,
  descreveHorarioParaLeitorDeTela,
  descreveOcupacao,
  estadoDoHorario,
  formataDiaPorExtenso,
  horaDoInicio,
  rotuloProfissional,
} from '../core/agendaHelpers'
import styles from '../core/styles/AgendaGrade.module.css'
import { IconPaths, KTSVG } from '_metronic/helpers'

type Props = {
  /** Resposta de `GET /agendamentos/disponibilidade`, dia a dia. */
  dias: DisponibilidadeDia[]
  carregando: boolean
  /** `inicio` do horário escolhido, ou `undefined`. */
  inicioSelecionado?: string
  onSelecionar: (horario: HorarioDisponivel) => void
  /** Texto do estado vazio — precisa dizer o próximo passo. */
  mensagemVazio: string
}

const ESTADOS_LEGENDA: EstadoHorario[] = ['livre', 'esgotado', 'excedente']

const chaveDoHorario = (horario: HorarioDisponivel): string =>
  `${horario.inicio}-${horario.profissional?.id ?? 'pool'}`

/**
 * Grade de horários projetados, agrupada por dia.
 *
 * Três regras que a revisão cobra e que estão implementadas aqui:
 *
 * 1. **A grade é montada só com o que veio.** Dia ausente da resposta não é erro
 *    nem horário livre — é ausência de oferta (escala, feriado ou horizonte), e
 *    a tela nunca sintetiza horário que a API não trouxe.
 * 2. **`disponivel` é a decisão do servidor**: o botão é desabilitado por ele,
 *    nunca por `capacidade - ocupadas` calculado aqui.
 * 3. **Cor nunca sozinha**: cada horário traz ícone + rótulo de estado + frase
 *    de ocupação, e o nome acessível do botão repete os três.
 */
export const AgendaGradeHorarios: FC<Props> = ({
  dias,
  carregando,
  inicioSelecionado,
  onSelecionar,
  mensagemVazio,
}) => {
  const totalHorarios = useMemo(
    () => dias.reduce((total, dia) => total + (dia.horarios?.length ?? 0), 0),
    [dias]
  )

  return (
    // `aria-live` + `aria-busy` anunciam a recarga (troca de filtro ou 409) —
    // sem isso a grade se atualiza em silêncio para quem usa leitor de tela.
    <div aria-live='polite' aria-busy={carregando}>
      <p className='visually-hidden'>
        {carregando
          ? 'Carregando horários disponíveis.'
          : `Grade atualizada: ${totalHorarios} ${
              totalHorarios === 1 ? 'horário' : 'horários'
            } no período.`}
      </p>

      {!carregando && totalHorarios === 0 && (
        <div
          className='d-flex align-items-start rounded border bg-gray-100 text-gray-900 p-4 mb-0'
          role='status'
        >
          <KTSVG
            path={IconPaths.CalendarX}
            className='svg-icon-em fs-4 me-3 text-gray-700'
            ariaHidden
          />
          <div>
            <strong className='d-block mb-1'>Nenhum horário no período</strong>
            <span className='text-gray-700'>{mensagemVazio}</span>
          </div>
        </div>
      )}

      {dias.map((dia) => (
        <section key={dia.data} className='mb-4' aria-label={formataDiaPorExtenso(dia.data)}>
          {/* h4: fica abaixo do h3 "Horários" do modal que monta a grade. */}
          <h4 className='fs-8 fw-semibold text-gray-700 mb-2'>{formataDiaPorExtenso(dia.data)}</h4>

          {!dia.horarios?.length ? (
            <p className='text-gray-700 mb-0'>Sem oferta neste dia.</p>
          ) : (
            <ul className={`${styles.grade} gap-2 list-unstyled m-0 p-0`}>
              {dia.horarios.map((horario) => {
                const estado = estadoDoHorario(horario)
                const selecionado = horario.inicio === inicioSelecionado
                const descricao = descreveHorarioParaLeitorDeTela(horario)

                return (
                  <li key={chaveDoHorario(horario)}>
                    <button
                      type='button'
                      className={`${styles.horario} ${styles[estado]} ${
                        selecionado ? styles.selecionado : ''
                      }`}
                      // Só o servidor decide se cabe reserva.
                      disabled={!horario.disponivel}
                      aria-pressed={selecionado}
                      aria-label={descricao}
                      title={descricao}
                      onClick={() => onSelecionar(horario)}
                    >
                      <span
                        className={`${styles.hora} d-flex align-items-center gap-1 fw-semibold`}
                      >
                        <i className={`bi ${ESTADO_HORARIO_ICONES[estado]}`} aria-hidden='true' />
                        {horaDoInicio(horario.inicio)}
                        {horario.duracaoMinutos ? ` · ${horario.duracaoMinutos} min` : ''}
                      </span>
                      <span className='d-block fw-semibold'>{ESTADO_HORARIO_LABELS[estado]}</span>
                      <span className='d-block text-truncate'>{descreveOcupacao(horario)}</span>
                      <span className='d-block text-truncate'>{rotuloProfissional(horario)}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      ))}

      {totalHorarios > 0 && (
        <ul
          className='d-flex flex-wrap align-items-center gap-3 list-unstyled m-0 p-0'
          aria-label='Legenda dos estados do horário'
        >
          {ESTADOS_LEGENDA.map((estado) => (
            <li key={estado} className={`${styles.itemLegenda} ${styles[estado]}`}>
              <i className={`bi ${ESTADO_HORARIO_ICONES[estado]}`} aria-hidden='true' />
              {ESTADO_HORARIO_LABELS[estado]}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
